/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  Asset,
  Ducks,
  DynamicChannel,
  DynamicChannelsApi,
  XError,
} from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, race, select, take, takeLeading } from 'redux-saga/effects';
import { StoreState } from 'store/store';
import { Rail } from 'types/Rail';
import * as Assets from 'core/store/ducks/assets';
import Constants from 'configs/Constants';
import RailBadgeEngine from 'core/services/core/badgeEngine/RailBadgeEngine';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import * as Context from './context';

const BADGES_PRIORITY = BADGES_PRIORITY_PER_SECTION_RAIL.ONDEMAND.RAILS;

const ADDITIONAL_ASSETS_TO_REQUEST = 1;

export enum ActionType {
  INIT = 'app/folderInto/rails/INIT',
  INIT_RAIL = 'app/folderInto/rails/INIT_RAIL',
  INIT_RAILS_DONE = 'app/folderInto/rails/INIT_RAILS_DONE',
  SUCCESS = 'app/folderInto/rails/SUCCESS',
  LOAD_MORE_ASSETS = 'app/folderInto/rails/LOAD_MORE_ASSETS',
  ERROR = 'app/folderInto/rails/ERROR',
}

interface LoadAssetsSettings {
  gridMode?: boolean;
  nextPageIndex?: number;
}

interface BaseMetadata {
  context: Context.FolderInfoContext;
  channelId: number;
}

type Init = ActionRequest<
  ActionType.INIT,
  { context: Context.FolderInfoContext; parentContext?: Context.ParentContext; channelId?: number }
>;
type InitRail = ActionSuccess<ActionType.INIT_RAIL, Rail, BaseMetadata>;
type InitRailsDone = ActionRequest<ActionType.INIT_RAILS_DONE, Context.FolderInfoContext>;
type Success = ActionSuccess<ActionType.SUCCESS, Asset[], BaseMetadata>;
type LoadMoreAssets = ActionRequest<ActionType.LOAD_MORE_ASSETS, BaseMetadata & { nextPageIndex: number }>;
type Error = ActionFail<ActionType.ERROR, BaseMetadata>;

type Actions = Init | InitRail | InitRailsDone | LoadMoreAssets | Success | Error | Context.ClearContext;

type ReducerState = Record<string, { data: Rail[]; processed: boolean; processing: boolean }>;

const Actions = {
  init: (context: Context.FolderInfoContext, parentContext?: Context.ParentContext, channelId?: number): Init => ({
    type: ActionType.INIT,
    meta: { context, parentContext, channelId },
  }),
  initRail: (context: Context.FolderInfoContext, channelId: number, rail: Rail): InitRail => ({
    type: ActionType.INIT_RAIL,
    meta: { context, channelId },
    payload: rail,
  }),
  initRailsDone: (context: Context.FolderInfoContext): InitRailsDone => ({
    type: ActionType.INIT_RAILS_DONE,
    meta: context,
  }),
  loadMoreAssets: (context: Context.FolderInfoContext, channelId: number, nextPageIndex: number): LoadMoreAssets => ({
    type: ActionType.LOAD_MORE_ASSETS,
    meta: { context, channelId, nextPageIndex },
  }),
  success: (context: Context.FolderInfoContext, channelId: number, assets: Asset[]): Success => ({
    type: ActionType.SUCCESS,
    meta: { context, channelId },
    payload: assets,
  }),
  error: (context: Context.FolderInfoContext, channelId: number, error: XError): Error => ({
    type: ActionType.ERROR,
    error,
    meta: { context, channelId },
  }),
};

export const ActionCreators = {
  init: Actions.init,
  loadMoreAssets: Actions.loadMoreAssets,
};

const initialState: ReducerState = {};

export default function rails(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.INIT: {
      const contextId = Context.getFolderContextId(action.meta.context);
      return {
        ...state,
        [contextId]: { data: [], processed: false, processing: true },
      };
    }
    case ActionType.INIT_RAIL: {
      const { context } = action.meta;
      const contextId = Context.getFolderContextId(context);
      return {
        ...state,
        [contextId]: {
          ...state[contextId],
          data: [
            ...state[contextId]?.data,
            {
              ...action.payload,
              loadingAssets: true,
              assets: [],
            },
          ],
        },
      };
    }
    case ActionType.INIT_RAILS_DONE: {
      const context = action.meta;
      const contextId = Context.getFolderContextId(context);
      return {
        ...state,
        [contextId]: {
          ...state[contextId],
          processed: true,
          processing: false,
        },
      };
    }
    case ActionType.LOAD_MORE_ASSETS: {
      const { context, channelId } = action.meta;
      const contextId = Context.getFolderContextId(context);

      return {
        ...state,
        [contextId]: {
          ...state[contextId],
          data: state[contextId]?.data?.map((rail) => {
            if (channelId !== rail.id) {
              return rail;
            }

            return {
              ...rail,
              loadingAssets: true,
            };
          }),
        },
      };
    }
    case ActionType.SUCCESS: {
      const { context, channelId } = action.meta;
      const assets = action.payload;

      const contextId = Context.getFolderContextId(context);

      return {
        ...state,
        [contextId]: {
          ...state[contextId],
          data: state[contextId]?.data?.map((rail) => {
            if (channelId !== rail.id) {
              return rail;
            }
            return {
              ...rail,
              loadingAssets: false,
              assets,
              hasMore: assets.length > Constants.MAX_ASSETS_PER_RAIL,
            };
          }),
        },
      };
    }
    case ActionType.ERROR: {
      const { context, channelId } = action.meta;
      const contextId = Context.getFolderContextId(context);

      return {
        ...state,
        [contextId]: {
          ...state[contextId],
          data: state[contextId]?.data?.map((rail) => {
            if (channelId !== rail.id) {
              return rail;
            }
            return {
              ...rail,
              loadingAssets: false,
              assets: [],
            };
          }),
        },
      };
    }
    case Context.ActionType.CLEAR: {
      const contextId = Context.getFolderContextId(action.meta);

      const newState = { ...state };
      delete newState[contextId];

      return newState;
    }
    default:
      return state;
  }
}

export const Selectors = {
  getRails: (state: StoreState, context: Context.FolderInfoContext) =>
    state.folderInfo.rails[Context.getFolderContextId(context)]?.data || [],
  getRail: (state: StoreState, context: Context.FolderInfoContext, channelId: number) =>
    state.folderInfo.rails[Context.getFolderContextId(context)]?.data?.find((rail) => rail.channelId === channelId),
  isProcessed: (state: StoreState, context: Context.FolderInfoContext) =>
    state.folderInfo.rails[Context.getFolderContextId(context)]?.processed === true,
  isProcessing: (state: StoreState, context: Context.FolderInfoContext) =>
    state.folderInfo.rails[Context.getFolderContextId(context)]?.processing === true,
};

// AUX
export function takeAssets(actionType: Assets.ActionTypes.SUCCESS | Assets.ActionTypes.FAIL, channelId: number): any {
  return take((act: any) => act.type === actionType && act.meta.id === channelId);
}

function* loadAssets(ctx: Context.FolderInfoContext, channelId: number, settings?: LoadAssetsSettings): SagaIterator {
  const { gridMode, nextPageIndex = 0 } = settings || {};
  const assetsToLoad = gridMode
    ? Constants.ASSETS_PER_GRID_PAGE
    : Constants.MAX_ASSETS_PER_RAIL + ADDITIONAL_ASSETS_TO_REQUEST;

  yield put(Assets.Creators.loadAssets(channelId, nextPageIndex, assetsToLoad));

  const [, error] = yield race([
    takeAssets(Assets.ActionTypes.SUCCESS, channelId),
    takeAssets(Assets.ActionTypes.FAIL, channelId),
  ]);

  if (error) {
    yield put(Actions.error(ctx, channelId, XError.fromError(error)));
    return;
  }

  const assets: Asset[] = (yield select(Assets.Selectors.getAssets, channelId)) || [];

  const assetsWithBadges: Asset[] = yield call(
    RailBadgeEngine.getAssetsWithBadgesListFromRail,
    assets,
    BADGES_PRIORITY
  );

  yield put(Actions.success(ctx, channelId, assetsWithBadges || []));
}

function* initRail(ctx: Context.FolderInfoContext, channelId: number, gridMode?: boolean): SagaIterator {
  try {
    const dynamicChannel: DynamicChannel | undefined = yield Ducks.Api.callApi(
      DynamicChannelsApi.getDynamicChannel,
      channelId
    );

    if (!dynamicChannel) {
      return;
    }

    yield put(
      Actions.initRail(ctx, channelId, {
        id: dynamicChannel.id,
        title: dynamicChannel.name,
        channelId,
        assets: [],
      })
    );

    yield fork(loadAssets, ctx, channelId, { gridMode });
  } catch (error) {
    yield put(Actions.error(ctx, channelId, XError.fromError(error)));
  }
}

function* loadMore(action: LoadMoreAssets): SagaIterator {
  const { context, channelId, nextPageIndex } = action.meta;

  yield call(loadAssets, context, channelId, { gridMode: true, nextPageIndex });
}

function* init(action: Init): SagaIterator {
  const { context, channelId: channelIdToInit } = action.meta;

  if (channelIdToInit) {
    yield call(initRail, context, channelIdToInit, true);
    yield put(Actions.initRailsDone(context));
    return;
  }

  for (let index = 0; index < (context.entryAsset?.channels || []).length; index += 1) {
    const channelId = context.entryAsset?.channels[index];

    if (channelId) {
      yield call(initRail, context, channelId);
    }
  }

  yield put(Actions.initRailsDone(context));
}

export function* sagas() {
  yield all([takeLeading(ActionType.INIT, init), takeLeading(ActionType.LOAD_MORE_ASSETS, loadMore)]);
}
